import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { toast } from "react-toastify";
import { getSocket } from "../services/socket";
import GroupListPanel from "../components/broadcast/GroupListPanel";
import ChatPanel from "../components/broadcast/ChatPanel";

// ── Page ──────────────────────────────────────────────────────────────────────

const BroadcastList = () => {
  const navigate = useNavigate();

  const [groups, setGroups]         = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState("");
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [mobileView, setMobileView]       = useState("list");
  const [showContacts, setShowContacts]   = useState(false);

  // Broadcasts keyed by groupId — computed once, passed down
  const broadcastsByGroup = useMemo(() =>
    broadcasts.reduce((acc, b) => {
      if (!acc[b.groupId]) acc[b.groupId] = [];
      acc[b.groupId].push(b);
      return acc;
    }, {}),
  [broadcasts]);

  // Broadcasts for selected group: oldest first so newest appears at bottom
  const groupBroadcasts = selectedGroup
    ? (broadcastsByGroup[selectedGroup.id] || []).slice().reverse()
    : [];

  const refreshBroadcasts = useCallback(() => {
    api.get("/broadcasts").then((r) => setBroadcasts(r.data.data || [])).catch(() => {});
  }, []);

  // Initial load
  useEffect(() => {
    Promise.all([api.get("/groups"), api.get("/broadcasts")])
      .then(([gr, br]) => {
        setGroups(gr.data.groups || gr.data.data || []);
        setBroadcasts(br.data.data || []);
      })
      .catch(() => toast.error("Failed to load data"))
      .finally(() => setLoading(false));
  }, []);

  // Socket: real-time broadcast status and delivery stat updates
  useEffect(() => {
    const socket = getSocket();

    // Worker finished — patch status + final sent/failed counts
    const handleUpdate = (update) => {
      setBroadcasts((prev) =>
        prev.map((b) => b.id === update.id ? { ...b, ...update } : b)
      );
    };

    // Webhook delivered/read/failed — increment the matching counter by 1
    const handleStats = ({ id, field }) => {
      setBroadcasts((prev) =>
        prev.map((b) =>
          b.id === id ? { ...b, [field]: (b[field] ?? 0) + 1 } : b
        )
      );
    };

    socket.on("broadcast_update", handleUpdate);
    socket.on("broadcast_stats",  handleStats);
    return () => {
      socket.off("broadcast_update", handleUpdate);
      socket.off("broadcast_stats",  handleStats);
    };
  }, []);

  // Poll: fast while sending (delivery receipt updates), slow otherwise
  useEffect(() => {
    const interval = broadcasts.some((b) => b.status === "sending") ? 5000 : 30000;
    const id = setInterval(refreshBroadcasts, interval);
    return () => clearInterval(id);
  }, [broadcasts, refreshBroadcasts]);

  const handleSelectGroup = (g) => {
    setSelectedGroup(g);
    setShowContacts(false);
    setMobileView("chat");
  };

  const handleNewBroadcast = () => {
    navigate("/broadcast/create", selectedGroup ? { state: { group: selectedGroup } } : {});
  };

  return (
    <div className="flex h-[calc(100vh-64px)] overflow-hidden">
      <GroupListPanel
        loading={loading}
        groups={groups}
        broadcasts={broadcasts}
        search={search}
        onSearch={setSearch}
        selectedGroup={selectedGroup}
        onSelectGroup={handleSelectGroup}
        onNewBroadcast={handleNewBroadcast}
        mobileView={mobileView}
      />
      <ChatPanel
        selectedGroup={selectedGroup}
        groupBroadcasts={groupBroadcasts}
        showContacts={showContacts}
        onToggleContacts={setShowContacts}
        onBack={() => setMobileView("list")}
        onNewBroadcast={handleNewBroadcast}
        mobileView={mobileView}
      />
    </div>
  );
};

export default BroadcastList;
